import React from 'react';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';
import { pt, pb, px, py, mt, mb, my } from 'styled-components-spacing';
import { map } from 'styled-components-breakpoint';
import { colors, Text, Heading } from 'themes/components';

import ApproveIcon from './icons/ApproveIcon';
import ReviewIcon from './icons/ReviewIcon';
import PreviewIcon from './icons/PreviewIcon';
import TagIcon from './icons/TagIcon';
import LibraryIcon from './icons/LibraryIcon';
import SafeIcon from './icons/SafeIcon';
import FileIcon from './icons/FileIcon';
import CloudIcon from './icons/CloudIcon';
import LocalIcon from './icons/LocalIcon';
import ChangeRequestIcon from './icons/ChangeRequestIcon';
import AnnotationIcon from './icons/AnnotationIcon';
import ImageContent from 'components/ImageContent';

const Wrapper = styled.div`
  ${px(2)};
  ${py({ mobile: 5, desktop: 8, xl: 10 })};
  ${map(
    {
      tablet: '1000px',
      desktop: '1200px'
    },
    (mw) => `max-width: ${mw};`
  )};
  margin: 0 auto;
`;

const Content = styled.div`
  & > h2 {
    ${mb(0)}
    ${mt(1)}
  }
`;

const FeaturesCallout = styled.div`
  ${mt(3)};
  display: grid;
  ${map(
    {
      mobile: '1fr',
      tablet: '1fr 1fr',
      desktop: '1fr 1fr'
    },
    (cols) => `grid-template-columns: ${cols};`
  )};
  grid-gap: 24px;
`;

const FeatureUnit = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 20px auto;
  h4 {
    color: ${colors.grey5};
    ${mb(0)}
  }
  p {
    color: ${colors.grey30};
  }
`;

const CloudStorageVisual = () => (
  <StaticImage
    src="./images/cloud-storage-featured-image.jpg"
    alt="Cloud storage interface"
    width={1000}
    quality={100}
    placeholder="blurred"
    formats={['auto', 'webp', 'avif']}
    style={{ borderRadius: '8px' }}
  />
);

const CloudStorageContent = () => (
  <Content>
    <StaticImage src="./images/cloud-storage.png" width={44} />
    <Heading variant="h2" as="h2" style={{ color: colors.pink30 }}>
      Cloud storage for game studios.
    </Heading>
    <Text variant="subhead" style={{ color: colors.grey20 }}>
      You need a centralized repository for your team’s source files. Google
      Drive and Dropbox don’t cut it as you grow. Mudstack is built for game
      studios.
    </Text>
    <FeaturesCallout>
      <FeatureUnit>
        <PreviewIcon fillColor={colors.pink30} />
        <div>
          <Heading variant="h4">Preview 3D files</Heading>
          <Text>
            View FBX, OBJ, GLTF (and many other formats) without downloading and
            opening.
          </Text>
        </div>
      </FeatureUnit>
      <FeatureUnit>
        <TagIcon fillColor={colors.pink30} />
        <div>
          <Heading variant="h4">Tag all the things</Heading>
          <Text>
            Find what you need fast when you can tag and filter files for easy
            searching.
          </Text>
        </div>
      </FeatureUnit>
      <FeatureUnit>
        <PreviewIcon fillColor={colors.pink30} />
        <div>
          <Heading variant="h4">Access controls</Heading>
          <Text>
            Control who sees your content, keeping your IP safe when working
            with external vendors.
          </Text>
        </div>
      </FeatureUnit>
      <FeatureUnit>
        <LibraryIcon fillColor={colors.pink30} />
        <div>
          <Heading variant="h4">Libraries</Heading>
          <Text>
            Group files in multiple collections without duplicating them or
            worrying about folder structures.
          </Text>
        </div>
      </FeatureUnit>
    </FeaturesCallout>
  </Content>
);

const ReviewFeedbackVisual = () => (
  <StaticImage
    src="./images/reviews-featured-image.jpg"
    alt="Reviews interface"
    width={1000}
    quality={100}
    placeholder="blurred"
    formats={['auto', 'webp', 'avif']}
    style={{ borderRadius: '8px' }}
  />
);

const ReviewFeedbackContent = () => (
  <Content>
    <StaticImage src="./images/review-feedback.png" width={44} />
    <Heading variant="h2" style={{ color: colors.yellow40 }}>
      Reviews & feedback.
    </Heading>
    <Text variant="subhead" style={{ color: colors.grey20 }}>
      Keep all your feedback centralized and associated with its file. Stop
      hunting for feedback in Slack, Miro, Trello or other tools that are
      disconnected from the art.
    </Text>
    <FeaturesCallout>
      <FeatureUnit>
        <ReviewIcon fillColor={colors.yellow40} />
        <div>
          <Heading variant="h4">Review requests</Heading>
          <Text>
            Artists can tag their leads when they're ready, no need to wait for
            a meeting.
          </Text>
        </div>
      </FeatureUnit>
      <FeatureUnit>
        <ApproveIcon fillColor={colors.yellow40} />
        <div>
          <Heading variant="h4">Approvals</Heading>
          <Text>
            Leads can approve work that meets the bar so artists can keep
            creating more.
          </Text>
        </div>
      </FeatureUnit>
      <FeatureUnit>
        <ChangeRequestIcon fillColor={colors.yellow40} />
        <div>
          <Heading variant="h4">Change requests</Heading>
          <Text>
            Easily assign change requests on files that need more work before
            they're ready to approve.
          </Text>
        </div>
      </FeatureUnit>
      <FeatureUnit>
        <AnnotationIcon fillColor={colors.yellow40} />
        <div>
          <Heading variant="h4">Annotations</Heading>
          <Text>
            Mark up files with our built in tools. Draw anywhere on screen to
            compose feedback.
          </Text>
        </div>
      </FeatureUnit>
    </FeaturesCallout>
  </Content>
);

const VersionControlVisual = () => (
  <StaticImage
    src="./images/versions-featured-image.jpg"
    alt="Unstaged changes interface"
    width={1000}
    quality={100}
    placeholder="blurred"
    formats={['auto', 'webp', 'avif']}
    style={{ borderRadius: '8px' }}
  />
);

const VersionControlContent = () => (
  <Content>
    <StaticImage src="./images/version-control.png" width={44} />
    <Heading variant="h2" style={{ color: colors.blue30 }}>
      Version control for artists.
    </Heading>
    <Text variant="subhead" style={{ color: colors.grey20 }}>
      Get file based version control that doesn't require locking and branching
      so your artists aren't forced to use developer tools.
    </Text>
    <FeaturesCallout>
      <FeatureUnit>
        <FileIcon fillColor={colors.blue30} />
        <div>
          <Heading variant="h4">File based versioning</Heading>
          <Text>
            File based, not project based. It's a lot easier when you're not
            dealing with irrelevant conflicts.
          </Text>
        </div>
      </FeatureUnit>
      <FeatureUnit>
        <SafeIcon fillColor={colors.blue30} />
        <div>
          <Heading variant="h4">Non-destructive</Heading>
          <Text>
            Roll back to older versions at any time without deleting or
            overwriting anything.
          </Text>
        </div>
      </FeatureUnit>
      <FeatureUnit>
        <CloudIcon fillColor={colors.blue30} />
        <div>
          <Heading variant="h4">Cloud versions</Heading>
          <Text>
            A single source of versions in the cloud for your team. View the
            history of each file.
          </Text>
        </div>
      </FeatureUnit>
      <FeatureUnit>
        <LocalIcon fillColor={colors.blue30} />
        <div>
          <Heading variant="h4">Local versions</Heading>
          <Text>
            Let mudstack manage versions locally as you save work. Share only
            the versions you need to.
          </Text>
        </div>
      </FeatureUnit>
    </FeaturesCallout>
  </Content>
);

const Features = () => (
  <Wrapper>
    <ImageContent
      ImgComponent={CloudStorageVisual}
      ContentComponent={CloudStorageContent}
      reverse
    />
    <ImageContent
      ImgComponent={ReviewFeedbackVisual}
      ContentComponent={ReviewFeedbackContent}
    />
    <ImageContent
      ImgComponent={VersionControlVisual}
      ContentComponent={VersionControlContent}
      reverse
    />
  </Wrapper>
);

export default Features;
